import { reducerWithInitialState } from "typescript-fsa-reducers";
import range from "lodash/range";
import compact from "lodash/compact";

import withImmer from "../../lib/withImmer";
import { generateClientId } from "../../lib/generateClientId";
import {
  bootstrapCompanyPosts,
  loadCompanyPosts,
  bootstrapCompanyShow,
  loadCompanyShow,
  loadNextPosts,
  loadPinnablePosts,
  draftPostPinUpdated,
  saveDraftPostPins,
  sendCompanyPost,
  draftReset,
} from "../actions";
import { CompanyPostsResponse, CompanyShowResponse, Post, PostSpace, CompanyPostPin, PinnablePost } from "../api";

interface PostPinningState {
  companyId: number;
  postPins: CompanyPostPin[];
  latestPinnablePosts: PinnablePost[];
  draftPostPins: CompanyPostPin[];
  featuredPosts: PinnablePost[];
  pinnablePosts: PinnablePost[];
  isPinnablePostsLoaded: boolean;
  isPinnablePostsLoading: boolean;
  isDraftPostPinsDirty: boolean;
}

const PostPinningInitialState: PostPinningState = {
  companyId: 0,
  postPins: [],
  latestPinnablePosts: [],
  draftPostPins: [],
  featuredPosts: [],
  pinnablePosts: [],
  isPinnablePostsLoaded: false,
  isPinnablePostsLoading: false,
  isDraftPostPinsDirty: false,
};

export type PostsState = {
  isLoaded: boolean;
  posts: Post[];
  spaces: PostSpace[];
  currentPage: number;
  isLoadingNextPosts: boolean;
  isAllPostsLoaded: boolean;
} & PostPinningState;

const InitialState: PostsState = {
  isLoaded: false,
  posts: [],
  spaces: [],
  currentPage: 1,
  isLoadingNextPosts: false,
  isAllPostsLoaded: false,
  ...PostPinningInitialState,
};

function initState(state: PostsState, result: CompanyPostsResponse) {
  const draftPostPins = result.post_pins;
  const featuredPosts = fillOutFeaturedPosts(draftPostPins, result.latest_pinnable_posts);
  Object.assign(state, {
    posts: result.posts,
    spaces: result.spaces,
    companyId: result.company.id,
    postPins: result.post_pins,
    latestPinnablePosts: result.latest_pinnable_posts,
    featuredPosts,
    draftPostPins,
    isLoaded: true,
  });
  return state;
}

function initStateFromBootstrapShow(state: PostsState, result: CompanyShowResponse) {
  const draftPostPins = result.post_pins;
  const featuredPosts = fillOutFeaturedPosts(draftPostPins, result.latest_pinnable_posts);
  Object.assign(state, {
    companyId: result.company.id,
    postPins: result.post_pins,
    latestPinnablePosts: result.latest_pinnable_posts,
    featuredPosts,
    draftPostPins,
  });
  return state;
}

function fillOutFeaturedPosts(draftPostPins: CompanyPostPin[], pinnablePosts: PinnablePost[]): PinnablePost[] {
  const pinnedPosts = compact(draftPostPins.map(p => (p.post ? p.post : null)));
  if (pinnedPosts.length === 3) {
    return pinnedPosts;
  }

  const pinnedPostIds = pinnedPosts.map(post => post.id);
  const unPinnedPosts = pinnablePosts.filter(post => !pinnedPostIds.includes(post.id));
  if (pinnedPosts.length === 0) {
    return unPinnedPosts.slice(0, 3);
  }

  const posts: PinnablePost[] = [];
  range(3).forEach(index => {
    const pin = draftPostPins.find(p => p.category_cd === index);
    if (pin && pin.post) {
      posts.push(pin.post);
    } else {
      const latestPost = unPinnedPosts.find(post => !posts.map(p => p.id).includes(post.id));
      if (latestPost) {
        posts.push(latestPost);
      }
    }
  });
  return posts;
}

const postsReducer = withImmer(reducerWithInitialState<PostsState>(InitialState))
  .case(bootstrapCompanyPosts, initState)
  .case(loadCompanyPosts.done, (state, { result: actionRes }) => {
    return initState(state, actionRes);
  })
  .case(bootstrapCompanyShow, initStateFromBootstrapShow)
  .case(loadCompanyShow.done, (state, { result: actionRes }) => {
    return initStateFromBootstrapShow(state, actionRes);
  })
  .case(loadNextPosts.started, state => {
    state.isLoadingNextPosts = true;
    return state;
  })
  .case(loadNextPosts.done, (state, { result }) => {
    Object.assign(state, {
      posts: state.posts.concat(result.posts),
      isLoadingNextPosts: false,
      currentPage: state.currentPage + 1,
      isAllPostsLoaded: result.meta.isLastPage,
    });
    return state;
  })
  .case(loadNextPosts.failed, state => {
    state.isLoadingNextPosts = false;
    return state;
  })
  .case(loadPinnablePosts.started, state => {
    state.isPinnablePostsLoading = true;
    return state;
  })
  .case(loadPinnablePosts.done, (state, { result }) => {
    Object.assign(state, {
      pinnablePosts: result,
      isPinnablePostsLoaded: true,
      isPinnablePostsLoading: false,
    });
    return state;
  })
  .case(loadPinnablePosts.failed, state => {
    state.isPinnablePostsLoading = false;
    return state;
  })
  .case(draftPostPinUpdated, (state, { postId, categoryCd }) => {
    const oldPin = state.draftPostPins.find(p => p.category_cd === categoryCd);
    const pinnedPost = postId ? state.pinnablePosts.find(p => p.id === postId) || null : null;
    const newPin: CompanyPostPin = {
      id: generateClientId(),
      ...oldPin,
      company: { id: state.companyId },
      post: pinnedPost,
      category_cd: categoryCd,
    };
    const draftPostPins = state.draftPostPins
      .filter(p => p.category_cd !== newPin.category_cd)
      .concat(newPin)
      .sort((a, b) => a.category_cd - b.category_cd);
    const featuredPosts = fillOutFeaturedPosts(draftPostPins, state.pinnablePosts);

    Object.assign(state, {
      draftPostPins,
      featuredPosts,
      isDraftPostPinsDirty: true,
    });
    return state;
  })
  .case(saveDraftPostPins.done, (state, { result: pins }) => {
    Object.assign(state, {
      postPins: pins,
      draftPostPins: pins,
      featuredPosts: fillOutFeaturedPosts(pins, state.latestPinnablePosts),
      isDraftPostPinsDirty: false,
    });
    return state;
  })
  .case(draftReset, state => {
    const draftPostPins = state.postPins;
    const featuredPosts = fillOutFeaturedPosts(draftPostPins, state.latestPinnablePosts);
    Object.assign(state, {
      draftPostPins,
      featuredPosts,
      isDraftPostPinsDirty: false,
    });
    return state;
  })
  .case(sendCompanyPost.done, (state, { result }) => {
    state.posts.unshift(result);
    return state;
  });

export default postsReducer;



// WEBPACK FOOTER //
// ./frontend/src/companies/reducers/posts.ts